import React, { useState, useEffect } from 'react';
import { FaTimes, FaCamera, FaSpinner } from 'react-icons/fa';
import api from '../services/api';
import SEO from '../components/SEO';

const Gallery = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [dbImages, setDbImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGallery = async () => {
            try {
                const res = await api.get('/gallery');
                if (res.data.success) {
                    setDbImages(res.data.data.map(img => ({
                        id: img._id,
                        src: img.imageUrl,
                        title: img.title,
                        category: img.category,
                        description: img.description
                    })));
                }
            } catch (err) {
                console.error("Error fetching gallery images", err);
            } finally {
                setLoading(false);
            }
        };
        fetchGallery();
    }, []);

    return (
        <div className="min-h-screen bg-maroon-dark sticky top-0">
            <SEO 
                title="Gallery • Taalsetu"
                description="A visual journey through Taalsetu's performances, workshops, and musical explorations."
                url="https://www.taalsetu.com/gallery/"
            />
            {/* Background Gradient */}
            <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-maroon-light via-maroon-dark to-black opacity-80 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-block p-4 rounded-full border-2 border-gold/30 bg-black/20 backdrop-blur-sm mb-6">
                        <FaCamera className="text-3xl text-gold" />
                    </div>
                    <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-light via-gold to-gold-dark mb-6 font-serif tracking-wide drop-shadow-sm">
                        Visual Journey
                    </h1>
                    <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto mb-6" />
                    <p className="text-lg text-cream/80 max-w-2xl mx-auto font-light leading-relaxed">
                        Experience the rhythm and soul through our lens. A curated collection of moments from performances, workshops, and musical explorations.
                    </p>
                </div>

                {/* Gallery Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <FaSpinner className="animate-spin text-5xl text-gold" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {dbImages.map((image) => (
                            <div
                                key={image.id}
                                className="group relative cursor-pointer"
                                onClick={() => setSelectedImage(image)}
                            >
                                <div className="relative aspect-w-4 aspect-h-3 rounded-xl overflow-hidden shadow-2xl border border-gold/20 bg-black/40 backdrop-blur-sm transition-all duration-500 group-hover:border-gold/60 group-hover:shadow-[0_0_30px_rgba(212,175,55,0.15)] flex items-center justify-center">
                                    <div className="absolute inset-0 bg-maroon-dark/20 group-hover:bg-transparent transition-colors duration-500 z-10 pointer-events-none" />
                                    <img
                                        src={image.src}
                                        alt={image.title || `${image.category || 'Tabla'} — Taalsetu Music Academy`}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100"
                                    />

                                    {/* Overlay Content */}
                                    <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
                                        <span className="text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-2">
                                            {image.category}
                                        </span>
                                        <h3 className="text-cream text-2xl font-serif font-medium">
                                            {image.title}
                                        </h3>
                                        <div className="w-12 h-0.5 bg-gold mt-4 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4 backdrop-blur-md"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        className="absolute top-6 right-6 text-gold/70 hover:text-gold transition-colors p-2 z-50 hover:bg-white/5 rounded-full"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(null);
                        }}
                    >
                        <FaTimes size={32} />
                    </button>

                    <div
                        className="relative max-w-6xl w-full max-h-[90vh] rounded-lg overflow-hidden flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="relative w-full flex justify-center border-2 border-gold/10 p-1 rounded-lg bg-black/50">
                            <img
                                src={selectedImage.src}
                                alt={selectedImage.title || `${selectedImage.category || 'Tabla'} — Taalsetu Music Academy`}
                                className="max-h-[80vh] w-auto object-contain rounded shadow-2xl"
                            />
                        </div>

                        <div className="mt-6 text-center">
                            <h3 className="text-gold text-3xl font-serif mb-2 tracking-wide">
                                {selectedImage.title}
                            </h3>
                            <p className="text-cream/60 text-sm uppercase tracking-widest border-t border-gold/20 pt-2 inline-block px-4">
                                {selectedImage.category}
                            </p>
                            {selectedImage.description && (
                                <p className="text-cream/80 mt-4 max-w-2xl mx-auto font-sans">
                                    {selectedImage.description}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;
